import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Star, Pencil, Trash2, FileText, Image as ImageIcon, Video as VideoIcon } from 'lucide-react'; // Added VideoIcon
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog'; // Import DeleteConfirmationDialog
import { casesApi } from './caseApi'; // Import the updated API
import { formatDate } from '../../utils/utils';
import { UserContext } from '../../context/UserContext'; // Import UserContext
import { useCases } from './CasesContext'; // Import useCases hook
import { canUserEditCase } from './medicalCasesUtils';
import { useFile } from '../../services/FileService';
import { isRenderableImage, isPlayableVideo, isVisualMedia } from '../../utils/media-types';
import CaseGallery from './CaseGallery'; // Import the new gallery component
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import PageTitle from '../../components/PageTitle';
import UploadErrors from '../../components/UploadErrors';
import { cancelButton, confirmButton } from '../../components/Buttons';
import './CaseView.scss';
import './CaseGallery.scss'; // Import gallery CSS


// Helper component to render media (image or video) with a signed URL for CaseView
const SignedMediaDisplay = ({ file, onClick }) => {
    const { signedUrl, loading, error } = useFile(file.url);
    // Only what the browser can draw or play — see utils/media-types.
    const isImage = isRenderableImage(file.contentType);
    const isVideo = isPlayableVideo(file.contentType);

    // Process filename to get the base name
    const rawFilename = file.filename || file.url;
    const baseFilename = rawFilename ? rawFilename.substring(rawFilename.lastIndexOf('/') + 1) : '';
    
    const shortFileName = baseFilename.length > 20 ? `${baseFilename.substring(0, 17)}...` : baseFilename;
    const defaultAlt = isImage ? 'תמונה' : isVideo ? 'וידאו' : 'קובץ';

    if (loading) {
        return <div className="file-item-placeholder" title={`טוען ${defaultAlt}...`}>{shortFileName}...</div>;
    }

    if (error) {
        console.error("Error loading signed URL for media:", file.url, error);
        return <div className="file-item-error" title={`שגיאה בטעינת ${defaultAlt}`}>{shortFileName} (שגיאה) ⚠️</div>;
    }

    if (isImage) {
        return (
            <img
                src={signedUrl}
                alt={baseFilename} // Use baseFilename for alt text
                className="file-item-image"
                onClick={onClick}
                title={`פתח ${defaultAlt}: ${baseFilename}`} // Use baseFilename for title
            />
        );
    } else if (isVideo) {
        return (
            <div className="file-item-video-container" onClick={onClick} title={`פתח ${defaultAlt}: ${baseFilename}`}> {/* Use baseFilename for title */}
                <VideoIcon size={48} className="file-item-doc-icon" />
                <span className="file-item-doc-name" title={baseFilename}> {/* Use baseFilename for title */}
                    {shortFileName} {/* shortFileName is already derived from baseFilename */}
                </span>
                {/* We don't use a <video> tag here directly to avoid auto-play and to make it clickable for gallery */}
            </div>
        );
    } else { // For documents (PDF, DOC, etc.)
        return (
            <a href={signedUrl} target="_blank" rel="noopener noreferrer" title={`פתח קובץ: ${baseFilename}`} className="file-item-doc-link"> {/* Use baseFilename for title */}
                <div className="file-item-doc">
                    <FileText size={48} className="file-item-doc-icon" />
                    <span className="file-item-doc-name" title={baseFilename}> {/* Use baseFilename for title */}
                        {shortFileName} {/* shortFileName is already derived from baseFilename */}
                    </span>
                </div>
            </a>
        );
    }
};

const CaseViewSkeleton = () => {
  return (
    <div className="case-view-container">
      <PageTitle title={<Skeleton.Text width="200px" />} />
      <div className="case-view-content">
        <div className="case-view-meta-string">
          <Skeleton.Text width="300px" />
        </div>
        <div className="case-view-additional-metadata">
          <div className="tags-container">
            <Skeleton.Text width="50px" /> <Skeleton.Text width="70px" /> <Skeleton.Text width="60px" />
          </div>
        </div>
        <div className="case-view-description">
          <Skeleton.Block height="100px" />
        </div>
      </div>
      <div className="case-view-assets file-display-section">
        <div className="case-view-assets-title">
          <Skeleton.Text width="150px" />
        </div>
        <div className="file-display-area">
          <Skeleton.Block height="100px" />
        </div>
      </div>
    </div>
  );
};

const REVIEW_STATE_LABELS = {
  'in-review': 'ממתין לבקרה',
  reviewed: 'אושר',
  blocked: 'נחסם',
};

const CaseView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext); // Get current user
  // Get deleteCaseAndHandleSuccess from context
  const { deleteCaseAndHandleSuccess } = useCases(); 

  const [medicalCase, setMedicalCase] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Renamed from loading
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false); // State for the new gallery
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0); // State for initial index
  const [isConfirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false); // State for delete confirmation
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  // Fetch data on mount or when id changes
  useEffect(() => {
    const fetchCase = async () => {
      setIsLoading(true); // Renamed from setLoading
      setError(null);
      try {
        const data = await casesApi.get(id);
        setMedicalCase(data);
        // Check if the current user has favorited this report
        if (user && data.favoritedBy?.includes(user._id)) {
          setIsFavorite(true);
        } else {
          setIsFavorite(false);
        }
        // Open gallery by default if any visual media (image or video) exists.
        // Not before the case is reviewed: it covers the page, and with it the review banner.
        const visualMediaFiles = data.files?.filter(file => isVisualMedia(file.contentType)) || [];
        if (visualMediaFiles.length > 0 && data.reviewState === 'reviewed') {
            setIsGalleryOpen(true);
            setGalleryInitialIndex(0); // Start at the first visual media item
        }
      } catch (err) {
        console.error('Error fetching case:', err);
        setError('אירעה שגיאה בטעינת המקרה');
        if (err.response?.status === 404) {
           setError('המקרה לא נמצא');
        }
      } finally {
        setIsLoading(false); // Renamed from setLoading
      }
    };

    fetchCase();
  }, [id, user]); // Re-run if id or user changes

  // --- Action Handlers ---

  const handleEditClick = () => {
    // Correct navigation path to match Routes.jsx
    navigate(`/cases/${medicalCase._id}/edit`);
  };

  const handleDelete = () => {
    if (medicalCase) {
      setConfirmDeleteDialogOpen(true);
    }
  };

  const confirmDeleteHandler = async () => {
    if (medicalCase) {
      // Close the local dialog immediately.
      // The actual deletion and navigation will be handled by the context function.
      setConfirmDeleteDialogOpen(false); 
      await deleteCaseAndHandleSuccess(medicalCase, () => {
        navigate('/cases'); // Navigate after successful deletion and refetch by context
      });
    } else {
      // Should not happen if button is only enabled when medicalCase exists, but good practice
      setConfirmDeleteDialogOpen(false); 
    }
  };

  const handleToggleFavorite = async () => {
    if (!medicalCase || !user) return; // Ensure report and user are loaded

    const originalFavoriteStatus = isFavorite;
    // Optimistic UI update
    setIsFavorite(!isFavorite);

    try {
      if (originalFavoriteStatus) {
        await casesApi.unfavorite(medicalCase._id);
      } else {
        await casesApi.favorite(medicalCase._id);
      }
      // Optionally refetch data to confirm, or rely on optimistic update
      // const updatedData = await casesApi.get(id);
      // setMedicalCase(updatedData);
      // setIsFavorite(updatedData.favoritedBy?.includes(user._id));

    } catch (err) {
      console.error('Error updating favorite status:', err);
      // Revert optimistic update on error
      setIsFavorite(originalFavoriteStatus);
      setError('אירעה שגיאה בעדכון המועדפים');
    }
  };

  const handleReview = async (decision) => {
    setIsReviewing(true);
    setReviewError(null);
    try {
      const { reviewState } = await casesApi.review(medicalCase._id, decision, medicalCase.lastModified);
      setMedicalCase(prev => ({ ...prev, reviewState }));
    } catch (err) {
      setReviewError(err?.serverError?.hebMessage || 'אירעה שגיאה בשמירת הבקרה. אנא נסה שנית.');
      // The case changed while it was on screen: show what there is to decide on now.
      if (err.response?.status === 409) {
        setMedicalCase(await casesApi.get(medicalCase._id));
      }
    } finally {
      setIsReviewing(false);
    }
  };

  // --- Gallery Handlers ---
  // Prepare all visual media items (images and videos) for the gallery
  const galleryMediaItems = medicalCase?.files?.filter(file => isVisualMedia(file.contentType)) || [];

  const openGallery = (clickedFileUrl) => {
    const itemIndex = galleryMediaItems.findIndex(item => item.url === clickedFileUrl);
    if (itemIndex !== -1) {
      setGalleryInitialIndex(itemIndex);
      setIsGalleryOpen(true);
    }
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  // --- Render Logic ---

  // Error or not found state handling
  if (!isLoading && (error || !medicalCase)) {
    return (
        <div className="case-view-container error-state">
          <h2>{error || 'המקרה לא נמצא'}</h2>
          <Link to="/cases" className="button-primary">
            חזרה לרשימת המקרים
          </Link>
        </div>
    );
  }

  const canModify = canUserEditCase(user, medicalCase);

  return (
    <div className="case-view-container">
      {isLoading ? (
        <CaseViewSkeleton />
      ) : (
        <>
          {/* Display API errors if not loading and medicalCase might be null due to error */}
          {error && !medicalCase && <div className="form-error">{error}</div>}

          {medicalCase && ( // Only render case details if medicalCase is available
            <>
              <PageTitle title={medicalCase.name}>
                {canModify && (
                  <>
                    <button
                      className="case-card-action edit"
                      onClick={handleEditClick}
                      aria-label="ערוך מקרה"
                      title="ערוך מקרה"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="case-card-action delete"
                      onClick={handleDelete}
                      aria-label="מחק מקרה"
                      title="מחק מקרה"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
                <button
                  className={`case-card-action favorite ${isFavorite ? 'favorited' : ''}`}
                  onClick={handleToggleFavorite}
                  aria-label={isFavorite ? "הסר ממועדפים" : "הוסף למועדפים"}
                  title={isFavorite ? "הסר ממועדפים" : "הוסף למועדפים"}
                  disabled={!user}
                >
                  <Star size={16} fill={isFavorite ? '#f4c741' : 'none'} />
                </button>
              </PageTitle>
              {medicalCase.reviewState === 'in-review' && !user?.isSuperAdmin && (
                <div className="case-review-banner in-review" role="status">
                  המידע שהעלית עובר בקרה שהוא לא מכיל מידע של מטופל. כרגע, המידע חשוף רק לך. כשהבקרה תאושר, כל חברי המחלקה יוכלו לצפות בו.
                </div>
              )}
              {medicalCase.reviewState === 'blocked' && (
                <div className="case-review-banner blocked" role="status">
                  {medicalCase.name} עלול להכיל מידע של מטופל. נא להוריד מהתיעוד (הן מקבצי המדיה המצורפים והן מתיאור המקרה) כל איזכור שעלול לזהות את המטופל.
                </div>
              )}
              {user?.isSuperAdmin && (
                <div className="case-review-panel">
                  <div>סטטוס בקרה: <strong>{REVIEW_STATE_LABELS[medicalCase.reviewState]}</strong></div>
                  <div className="case-review-panel__actions">
                    {medicalCase.reviewState !== 'reviewed' && (
                      <button type="button" style={confirmButton} disabled={isReviewing} onClick={() => handleReview('approve')}>
                        אישור - חשיפה למחלקה
                      </button>
                    )}
                    {medicalCase.reviewState !== 'blocked' && (
                      <button type="button" style={cancelButton} disabled={isReviewing} onClick={() => handleReview('block')}>
                        חסימה
                      </button>
                    )}
                  </div>
                  <UploadErrors
                    messages={reviewError ? [reviewError] : []}
                    onDismiss={() => setReviewError(null)}
                    autoDismissMs={0}
                  />
                </div>
              )}
              <div className="case-view-content">
                <div className="case-view-meta-string">
                  נוצר ב-{formatDate(medicalCase.createdAt)}
                  {medicalCase.createdBy ? ` ע"י ${medicalCase.createdBy.name}` : ''}
                  {medicalCase.lastModified && medicalCase.lastModified !== medicalCase.createdAt &&
                    ` | עודכן ב-${formatDate(medicalCase.lastModified)}`}
                  {medicalCase.hospital ? ` | בית חולים ${medicalCase.hospital.nameHebrew || medicalCase.hospital.nameEnglish}` : ''}
                </div>

                <div className="case-view-additional-metadata">
                  {medicalCase.tags && medicalCase.tags.length > 0 && (
                    <div className="tags-container">
                      {medicalCase.tags.map((tag, index) => (
                        <span key={index} className="tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {medicalCase.description && (
                  <div className="case-view-description">{medicalCase.description}</div>
                )}
              </div>

              {/* Unified File Display Area - Adapted from CaseEditor */}
              {medicalCase.files && medicalCase.files.length > 0 && (
                <div className="case-view-assets file-display-section"> {/* Added file-display-section */}
                  <div className="case-view-assets-title">קבצים מצורפים:</div>
                  <div className="file-display-area">
                    {medicalCase.files.map((file, index) => {
                      const opensInGallery = isVisualMedia(file.contentType);

                      return (
                        <div key={file._id || file.url || index} className="file-item-container">
                          <SignedMediaDisplay
                            file={file}
                            onClick={opensInGallery ? () => openGallery(file.url) : undefined}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Render the CaseGallery component with all visual media items */}
              {isGalleryOpen && galleryMediaItems.length > 0 && (
                <CaseGallery
                  mediaItems={galleryMediaItems}
                  initialIndex={galleryInitialIndex}
                  onClose={closeGallery}
                  title={medicalCase.name}
                  description={medicalCase.description}
                />
              )}
            </>
          )}
        </>
      )}
      <DeleteConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        title={`האם אתה בטוח שברצונך למחוק את "${medicalCase?.name}"?`}
        onConfirm={confirmDeleteHandler}
        onClose={() => setConfirmDeleteDialogOpen(false)}
      />
    </div>
  );
};

// Removed SignedImage and SignedLink as their logic is incorporated into SignedMediaDisplay or CaseGallery's SignedMediaItem
export default CaseView;
